import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Spin, Button } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import useProposalSquareStyles, {
  cardColors,
} from '../styles/components/proposal/ProposalSquare';
import ExploreCard from '../components/explore/ExploreCard';
import FilterSystem from '../components/filter/FilterSystem';
import competitionService from '../services/competitionService';
import menuConfig from '../router/menuConfig';
import { getCurrentUser } from '../services/authService';

// Helper to map API content to card props
const mapContentToCard = (content) => {
  const fields = content.fields || {};
  
  // Handle author info
  let authorName = 'Unknown';
  let authorAvatar = '';
  
  if (content.user) {
    authorName = content.user.name || content.user.nickname || 'Unknown';
    authorAvatar = content.user.avatar || '';
  } else if (content.creator) {
    // Fallback if user object is not directly available but creator is
    authorName = content.creator.name || content.creator.nickname || 'Unknown';
    authorAvatar = content.creator.avatar || '';
  }
  
  // Process cover image URL
  let coverImage = null;
  if (fields.cover_image) {
    coverImage = fields.cover_image.startsWith('http') 
      ? fields.cover_image 
      : `/api/v2/contents/${fields.cover_image}/file`;
  }

  return {
    id: content.id,
    title: fields.title || fields.prompt || '无标题',
    description: fields.body_text || fields.response || fields.description || '',
    coverImage: coverImage,
    author: {
      name: authorName,
      avatar: authorAvatar
    },
    stats: {
      likes: content.interactions?.like_count || 0,
      is_liked: content.is_liked
    },
    tags: fields.tags || [],
    status: fields.status || content.status
  };
};

const TaskPage = () => {
  const { styles } = useProposalSquareStyles();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [currentFilter, setCurrentFilter] = useState(null);
  const [currentSort, setCurrentSort] = useState(null);
  const [currentGroup, setCurrentGroup] = useState(null);
  const observer = useRef();
  const [refreshFlag, setRefreshFlag] = useState(0);

  const currentUser = getCurrentUser && getCurrentUser();
  const userId = currentUser && currentUser.id ? currentUser.id : '';

  const location = useLocation();
  const navigate = useNavigate();

  // 从URL中获取搜索参数
  const getSearchFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get('search') || '';
  };

  const [searchKeyword, setSearchKeyword] = useState(getSearchFromUrl());

  // 监听URL参数变化
  useEffect(() => {
    const newSearchKeyword = getSearchFromUrl();
    if (newSearchKeyword !== searchKeyword) {
      setSearchKeyword(newSearchKeyword);
      setPage(1); // 重置页码
      setProposals([]); // 清空列表
      setHasMore(true);
      setLoading(true); // 触发数据刷新
      // 触发数据获取
      setRefreshFlag(flag => flag + 1);
    }
  }, [location.search, searchKeyword]);

  // 从localStorage中的菜单数据获取默认筛选条件
  const getDefaultFilterForPath = (path) => {
    try {
      // 优先从localStorage获取菜单数据
      const menuDataJson = localStorage.getItem('syntrust_menu_data');
      if (menuDataJson) {
        const menuData = JSON.parse(menuDataJson);
        for (const menu of menuData) {
          if (menu.children) {
            const menuItem = menu.children.find((child) => child.path === path);
            if (menuItem && menuItem.defaultFilter) {
              return menuItem.defaultFilter;
            }
          }
        }
      }

      // 如果localStorage中没有，则从menuConfig中获取作为fallback
      for (const menu of menuConfig) {
        if (menu.children) {
          const menuItem = menu.children.find((child) => child.path === path);
          if (menuItem && menuItem.defaultFilter) {
            return menuItem.defaultFilter;
          }
        }
      }
    } catch (error) {
      console.error('获取默认筛选条件失败:', error);
    }
    return null;
  };

  // 监听view_id参数变化 - 当从视图路由切换到默认菜单时
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const viewId = params.get('view_id');

    // 如果没有view_id参数，说明切换到了默认菜单
    if (!viewId && location.pathname === '/tasks') {
      // 从menuConfig中获取默认筛选条件（提案页面通常没有默认筛选条件）
      const defaultFilter = getDefaultFilterForPath(location.pathname);
      setCurrentFilter(defaultFilter);
      setCurrentSort(null);
      setCurrentGroup(null);
      setPage(1);
      setProposals([]);
      setHasMore(true);
      setLoading(true);
      // 触发数据获取
      setRefreshFlag(flag => flag + 1);
    }
  }, [location.search, location.pathname]);

  // 监听菜单跳转带过来的参数
  useEffect(() => {
    if (location.state) {
      // 如果是清空筛选条件的标志
      if (location.state.clearFilters) {
        setCurrentFilter(null);
        setCurrentSort(null);
        setCurrentGroup(null);
        setPage(1);
        setProposals([]);
        setHasMore(true);
        setLoading(true);
        // 触发数据获取
        setRefreshFlag(flag => flag + 1);
      }
      // 如果是视图参数，需要同时处理多个参数
      else if (location.state.applyViewFilters) {
        if (location.state.filterParams) {
          setCurrentFilter(location.state.filterParams);
        }
        if (location.state.sortParams) {
          setCurrentSort(location.state.sortParams);
        }
        if (location.state.groupParams) {
          setCurrentGroup(location.state.groupParams);
        }
        setPage(1);
        setProposals([]);
        setHasMore(true);
        setLoading(true);
        // 触发数据获取
        setRefreshFlag(flag => flag + 1);
      }
      // 如果只有筛选参数（默认菜单）
      else if (location.state.filterParams) {
        setCurrentFilter(location.state.filterParams);
        setPage(1);
        setProposals([]);
        setHasMore(true);
        setLoading(true);
        // 触发数据获取
        setRefreshFlag(flag => flag + 1);
      }
    }
  }, [location.state]);

  // 处理筛选变化
  const handleFilterChange = (filterParams) => {
    setCurrentFilter(filterParams);
    setPage(1); // 重置页码
    setProposals([]);
    setHasMore(true);
    setLoading(true); // 触发重新加载
    // 触发数据获取
    setRefreshFlag(flag => flag + 1);
  };

  // 处理排序变化
  const handleSortChange = (sortParams) => {
    setCurrentSort(sortParams);
    setPage(1); // 重置页码
    setProposals([]);
    setHasMore(true);
    setLoading(true); // 触发重新加载
    // 触发数据获取
    setRefreshFlag(flag => flag + 1);
  };

  // 滚动加载逻辑
  const lastProposalRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new window.IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
          setLoading(true);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );
  // 请求控制器引用
  const requestRef = useRef(null);

  // 加载数据 - 使用useCallback优化，避免不必要的重新创建
  const fetchData = useCallback(async () => {
    try {
      console.log('TaskPage fetchData called', { page, currentFilter, currentSort, searchKeyword });
      setLoading(true);
      
      // 构建筛选条件
      let combinedFilter = currentFilter;
      // 如果有搜索关键词，添加搜索筛选条件
      if (searchKeyword) {
        const searchFilter = {
          exprs: [
            {
              field: 'title',
              op: 'LIKE',
              values: [searchKeyword],
            },
          ],
        };
        if (currentFilter && currentFilter.exprs) {
          combinedFilter = {
            exprs: [...currentFilter.exprs, ...searchFilter.exprs],
          };
        } else {
          combinedFilter = searchFilter;
        }
      }
      // 构建搜索请求参数，使用POST /api/v2/contents/search
      const searchParams = {
        type: 'article',
        filter: {
          exprs: [
            {
              field: 'category',
              op: 'EQ',
              values: ['competition']
            },
            {
              field: 'author_id',
              op: 'EQ',
              values: [userId]
            }
          ]
        },
        pagination: {
          page: page,
          per_page: 20
        },
        sort: {
          field: 'inserted_at',
          desc: true
        }
      };

      // 如果有额外的筛选条件，合并到搜索参数中
      if (combinedFilter && combinedFilter.exprs) {
        searchParams.filter.exprs = [...searchParams.filter.exprs, ...combinedFilter.exprs];
      }

      // 取消之前的请求
      if (requestRef.current) {
        requestRef.current.abort();
      }

      // 创建新的请求
      requestRef.current = new AbortController();
      const signal = requestRef.current.signal;

      const response = await competitionService.searchMyEntries(searchParams, { signal });
      console.log('response', response);
      if (response && response.contents) {
        // 直接使用API返回的原始数据，只添加colorIdx用于卡片样式
        const formattedData = response.contents.map((item, idx) => ({
          ...item,
          colorIdx: (idx + ((page - 1) * 20)) % cardColors.length,
        }));

        setProposals(prev => page === 1 ? formattedData : [...prev, ...formattedData]);
        
        // 使用metadata中的分页信息判断是否还有更多数据
        if (response.metadata) {
          const { total, page: currentPage, per_page, total_pages } = response.metadata;
          setHasMore(currentPage < total_pages);
        } else {
          // 如果没有metadata，使用contents长度判断
          setHasMore(response.contents.length === 20);
        }
      } else {
        console.warn('响应格式不正确，缺少contents字段:', response);
        setHasMore(false);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('获取提案列表失败:', error);
        if (page === 1) {
          setProposals([]);
        }
        setHasMore(false);
      }
    } finally {
      setLoading(false);
      requestRef.current = null;
    }
  }, [page, currentFilter, currentSort, searchKeyword, userId]);
  // 使用useEffect监听fetchData的变化
  useEffect(() => {
    // 防抖处理
    const timeoutId = setTimeout(fetchData, 100);
    return () => {
      clearTimeout(timeoutId);
      if (requestRef.current) {
        requestRef.current.abort();
      }
    };
  }, [fetchData]);

  // 初始化数据获取
  useEffect(() => {
    console.log('TaskPage initial load effect triggered');
    // 如果还没有数据且没有加载中，则触发数据获取
    if (proposals.length === 0 && !loading) {
      console.log('TaskPage triggering initial data fetch');
      fetchData();
    }
  }, []); // 只在组件挂载时执行一次

  // 监听refreshFlag变化，强制刷新
  useEffect(() => {
    if (refreshFlag > 0) {
      console.log('TaskPage refreshFlag triggered:', refreshFlag);
      fetchData();
    }
  }, [refreshFlag, fetchData]);

  // 新增：外部触发刷新方法
  const refreshProposals = () => {
    setPage(1);
    setProposals([]);
    setHasMore(true);
    setLoading(true);
    // 触发数据获取
    setRefreshFlag(flag => flag + 1);
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.filterContainer}>
        <FilterSystem
          onFilterChange={handleFilterChange}
          onSortChange={handleSortChange}
          currentFilter={currentFilter}
          currentSort={currentSort}
          currentGroup={currentGroup}
          onProposalCreated={refreshProposals}
        />
      </div>

      <div className={styles.cardGrid}>
        {proposals.map((item, idx) => {
          // 如果是最后一个元素，添加ref用于滚动加载
          const isLast = idx === proposals.length - 1;
          const cardProps = mapContentToCard(item);
          return (
            <div key={item.id || idx} ref={isLast ? lastProposalRef : null} className="flex-shrink-0">
              <ExploreCard
                {...cardProps}
                layout="horizontal"
                className="!w-[416px] flex-shrink-0"
                onClick={() => navigate(`/explore/proposals/${item.id}`)}
              />
            </div>
          );
        })}
      </div>
      {loading && (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      )}
      {!loading && proposals.length === 0 && (
        <div style={{ textAlign: 'center', color: '#aaa', padding: 32 }}>
          {searchKeyword ? (
            <div>
              <div>没有找到包含 "{searchKeyword}" 的提案</div>
              <div style={{ fontSize: '14px', marginTop: '8px' }}>
                尝试使用其他关键词搜索
              </div>
            </div>
          ) : (
            '暂无提案数据'
          )}
        </div>
      )}
      {!hasMore && proposals.length > 0 && (
        <div style={{ textAlign: 'center', color: '#aaa', padding: 16 }}>
          没有更多了
        </div>
      )}
    </div>
  );
};

export default TaskPage;
